import {
  isFulfilled,
  isPending,
  isRejected,
  type Middleware,
} from '@reduxjs/toolkit';

import { trackAnalyticsEvent } from '~/common/analytics/analyticsClient';

type ApiCallAction = {
  type: string;
  meta: {
    arg: { endpointName: string; type: 'query' | 'mutation' };
    requestId: string;
  };
  payload?: unknown;
};

// Codegen-onboarded services (k2, esign, unified-fields, ...) use a
// `api/<service>` reducerPath, which RTK Query embeds as the action type
// prefix (`api/k2/executeQuery/pending`). The legacy `api` slice's
// reducerPath is `API Service`, which doesn't match, so it's left unqualified.
const SERVICE_REDUCER_PATH_PATTERN =
  /^api\/([^/]+)\/execute(?:Query|Mutation)\//;

function isApiCallAction(action: unknown): action is ApiCallAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'meta' in action &&
    typeof (action as { meta?: { arg?: { endpointName?: unknown } } }).meta?.arg
      ?.endpointName === 'string'
  );
}

function extractHttpStatus(payload: unknown): number | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;

  const { status, originalStatus, response } = payload as {
    status?: unknown;
    originalStatus?: unknown;
    response?: { status?: unknown };
  };

  if (typeof status === 'number') return status;
  if (typeof originalStatus === 'number') return originalStatus;
  if (typeof response?.status === 'number') return response.status;

  return undefined;
}

function resolveOperationName(actionType: string, endpointName: string) {
  const serviceName = SERVICE_REDUCER_PATH_PATTERN.exec(actionType)?.[1];
  return serviceName ? `${serviceName}/${endpointName}` : endpointName;
}

function buildEventName(operationName: string, phase: string) {
  return `api::${operationName}::${phase}`;
}

export const apiCallAnalyticsMiddleware: Middleware =
  () => (next) => (action) => {
    const result = next(action);

    if (isApiCallAction(action)) {
      const { endpointName, type } = action.meta.arg;
      const { requestId } = action.meta;
      const operationName = resolveOperationName(action.type, endpointName);
      const eventProperties = { endpointName: operationName, requestId, type };

      if (isPending(action)) {
        trackAnalyticsEvent(
          buildEventName(operationName, 'start'),
          eventProperties,
          'api-call',
        );
      } else if (isFulfilled(action)) {
        trackAnalyticsEvent(
          buildEventName(operationName, 'completed'),
          { ...eventProperties, outcome: 'success' },
          'api-call',
        );
      } else if (isRejected(action)) {
        const httpStatus = extractHttpStatus(action.payload);

        trackAnalyticsEvent(
          buildEventName(operationName, 'completed'),
          { ...eventProperties, httpStatus, outcome: 'failure' },
          'api-call',
        );
      }
    }

    return result;
  };
